import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { NavLink } from "@/components/NavLink";

const FAQ = () => {
  const faqCategories = [
    {
      category: "Ocean Freight",
      questions: [
        {
          q: "What's the difference between FCL and LCL?",
          a: "FCL (Full Container Load) means you rent an entire container exclusively for your cargo, ideal for larger shipments. LCL (Less than Container Load) means your cargo shares container space with other shippers, making it cost-effective for smaller volumes. FCL typically has faster transit times and less handling, while LCL offers flexibility for smaller quantities."
        },
        {
          q: "How long does ocean freight take from Athens?",
          a: "Transit times vary by destination: Athens to Rotterdam takes 7-10 days, Athens to Hamburg 8-11 days, Athens to New York 14-18 days, Athens to Shanghai 20-25 days, and Athens to Dubai 10-14 days. These are port-to-port times and don't include customs clearance or inland transportation."
        },
        {
          q: "What documents are required for ocean shipping?",
          a: "Standard documents include: Commercial Invoice, Packing List, Bill of Lading, Certificate of Origin, and relevant certifications depending on cargo type. For imports to certain countries, you may also need ISF filing, insurance certificates, and specific health/safety certificates. Our Athens team handles all documentation preparation."
        },
        {
          q: "Do you offer door-to-door ocean freight service?",
          a: "Yes, we provide complete door-to-door ocean freight services. This includes inland pickup from your facility, export customs clearance, ocean transit, import customs clearance at destination, and final delivery to the consignee. We handle all logistics coordination and documentation throughout the journey."
        }
      ]
    },
    {
      category: "Container Sales & Rental",
      questions: [
        {
          q: "What container grades do you offer?",
          a: "We offer three grades: Grade A (excellent condition, minimal wear, suitable for long-term use), Grade B (good condition with minor cosmetic issues, fully functional), and Grade C (economical option with visible wear but structurally sound). All containers undergo thorough inspection and come with certification."
        },
        {
          q: "Can I inspect a container before purchasing?",
          a: "Absolutely! We encourage inspections before purchase. You can schedule an appointment at our Athens or Piraeus depots to physically inspect any container. We also provide detailed photos and inspection reports for remote buyers. Inspection certificates are available for all units."
        },
        {
          q: "What are the rental terms?",
          a: "We offer flexible rental periods from 1 month to several years. Short-term rentals (1-6 months) have higher monthly rates but no long-term commitment. Long-term leases (6+ months) offer discounted rates. All rentals include basic maintenance, and you can choose between depot pickup or delivery to your site."
        },
        {
          q: "Do you offer specialized containers?",
          a: "Yes, we have a variety of specialized containers including reefers (refrigerated), open-top, flat rack, tank containers, and high cube units. Reefer containers come with temperature monitoring systems. We can also source custom modifications if needed for specific requirements."
        }
      ]
    },
    {
      category: "Warehousing",
      questions: [
        {
          q: "Where are your warehouses located?",
          a: "Our main warehouses are strategically located near Athens and Piraeus, providing easy access to the port and major highways. We have over 50,000 square meters of space across multiple facilities, including temperature-controlled zones and bonded warehouse areas."
        },
        {
          q: "Do you offer temperature-controlled storage?",
          a: "Yes, we have three temperature zones: ambient storage, chilled storage (2-8°C) for pharmaceuticals and perishables, and frozen storage (-18°C to -25°C). All temperature zones have continuous monitoring, backup power systems, and alarm notifications. We provide temperature mapping reports and certifications."
        },
        {
          q: "What value-added services do you provide?",
          a: "Our VAS include product labeling, kitting and bundling, quality control inspections, repackaging, product assembly, barcode labeling, shrink-wrapping, and customization services. We can also handle returns processing, product testing, and special packaging requirements."
        },
        {
          q: "How do I access my inventory information?",
          a: "You have 24/7 access to your inventory through our WMS (Warehouse Management System) portal. You can view real-time stock levels, movement history, pending orders, and detailed reports. We also offer API integration with your systems for automatic synchronization of inventory data."
        }
      ]
    },
    {
      category: "Customs & Documentation",
      questions: [
        {
          q: "Do you handle customs clearance?",
          a: "Yes, we provide full customs brokerage services at both origin and destination. Our licensed customs brokers handle HS classification, duty and tax calculation, electronic filing, and all regulatory compliance. We coordinate with customs authorities to ensure smooth clearance."
        },
        {
          q: "What is an HS code and why is it important?",
          a: "HS (Harmonized System) code is an internationally standardized system of names and numbers for classifying traded products. It's used by customs authorities worldwide to identify products, assess duties, and collect trade statistics. Correct classification is crucial for proper duty calculation and regulatory compliance."
        },
        {
          q: "How long does customs clearance typically take?",
          a: "Standard customs clearance in Greece takes 1-2 business days for routine shipments with proper documentation. Complex shipments requiring additional inspections or certifications may take 3-5 days. We expedite the process by pre-clearing documentation and maintaining good relationships with customs authorities."
        },
        {
          q: "Do you provide cargo insurance?",
          a: "Yes, we offer comprehensive marine cargo insurance options. Coverage includes all-risk protection for damage, loss, theft, and general average contributions. Insurance can cover door-to-door or specific segments of your shipment. Premiums are typically 0.5-2% of cargo value depending on commodity and route."
        }
      ]
    },
    {
      category: "Tracking & Support",
      questions: [
        {
          q: "How can I track my shipment?",
          a: "You can track shipments using our online tracking portal by entering your container number, bill of lading, or booking ID. Registered clients have access to our full tracking system with detailed milestones, vessel information, and ETA updates. You can also request tracking updates via email or phone from our operations team."
        },
        {
          q: "What if my shipment is delayed?",
          a: "We monitor all shipments actively and notify you immediately of any delays. Delays can occur due to weather, port congestion, or customs issues. Our Athens operations team works to minimize delays and arrange alternative solutions when possible. We provide regular updates and revised ETAs."
        },
        {
          q: "Is customer support available 24/7?",
          a: "Our Athens office operates Monday-Friday 8:00-18:00 EET with customer support available during these hours. For urgent operational issues, we have a 24/7 emergency hotline. Registered clients with active shipments can reach our on-call operations manager anytime."
        },
        {
          q: "How do I file a claim for damaged cargo?",
          a: "Claims should be reported immediately upon delivery. Document damage with photos and note it on the delivery receipt. Contact our claims department within 3 days with photos, original packing list, commercial invoice, and description of damage. If insured, we'll assist with the insurance claim process. Our team investigates all claims thoroughly."
        }
      ]
    },
    {
      category: "Pricing & Payment",
      questions: [
        {
          q: "How are shipping rates calculated?",
          a: "Rates depend on multiple factors: origin/destination, cargo weight and volume, container type, service level (standard/expedited), fuel surcharges, and peak season factors. We provide detailed quotes breaking down all costs including freight, documentation fees, terminal handling charges, and any applicable surcharges."
        },
        {
          q: "What payment terms do you offer?",
          a: "For new clients, we typically require prepayment or payment on departure. Established clients with good payment history can negotiate credit terms (typically 30 days). We accept bank transfers, credit cards, and for certain services, cryptocurrency payments. All payments are processed through secure channels."
        },
        {
          q: "Are there any hidden fees?",
          a: "No hidden fees. Our quotes include all standard charges. However, additional costs may apply for: demurrage (container detention beyond free time), port congestion surcharges, fuel surcharges (adjusted quarterly), customs duties and taxes, and optional services like extra insurance or special handling. We're transparent about all potential charges upfront."
        },
        {
          q: "Do you offer volume discounts?",
          a: "Yes, we offer competitive rates for high-volume shippers. If you ship multiple containers monthly or have regular shipment needs, contact our sales team for a customized rate agreement. Long-term contracts also receive preferential pricing and dedicated account management."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Frequently Asked Questions</h1>
            <p className="text-xl text-muted-foreground">
              Find answers to common questions about our shipping, warehousing, and logistics services. Can't find what you're looking for? Contact our Athens team.
            </p>
          </div>

          {/* FAQ Categories */}
          {faqCategories.map((category, idx) => (
            <Card key={idx} className="mb-8">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-6 text-primary">{category.category}</h2>
                <Accordion type="single" collapsible className="w-full">
                  {category.questions.map((item, qIdx) => (
                    <AccordionItem key={qIdx} value={`item-${idx}-${qIdx}`}>
                      <AccordionTrigger className="text-left text-lg font-semibold hover:text-primary">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-base leading-relaxed pt-4">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          ))}

          {/* CTA Section */}
          <Card className="bg-primary text-primary-foreground mt-12">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
              <p className="text-xl mb-8 opacity-90">
                Our Athens-based team is here to help. Contact us directly for personalized assistance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" asChild>
                  <NavLink to="/contact">Contact Support</NavLink>
                </Button>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary" asChild>
                  <NavLink to="/services">View All Services</NavLink>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQ;
